import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useForm, FormProvider } from 'react-hook-form';
import Modal from './Modal';
import InputPatient from '../Form/Fields/InputPatient';
import ImprovedSelect from '../Form/Fields/ImprovedSelect';
import Textarea from '../Form/Fields/Textarea';
import Button from '../Form/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../../api';
import './ExchangeModal.scss';

const formatAmount = (val) =>
    new Intl.NumberFormat('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val ?? 0);

// "Smart" return: the pharmacist picks a product (not an order) and a quantity — this finds
// every not-yet-fully-returned occurrence of that product across the patient's own orders and
// allocates the requested quantity against them automatically (oldest order first), splitting
// across several orders under the hood if a single one doesn't cover it. A return is always
// scoped to one order server-side, so this fires one POST per order actually involved.
//
// `patientId` is optional: pass it when opened from a patient's own page (skips straight to
// product selection); omit it (e.g. from the global /returns listing) to show a patient picker
// first.
export default function PatientSmartReturnModal({ patientId: fixedPatientId, show, onClose, onSave }) {
    const methods = useForm();
    const [pickedPatient, setPickedPatient] = useState(null); // { value, label } — only used when fixedPatientId isn't given
    const patientId = fixedPatientId ?? pickedPatient?.value ?? null;
    const [returnableItems, setReturnableItems] = useState([]);
    const [fetching, setFetching] = useState(false);
    const [lines, setLines] = useState([{ id: 'l0', orderableId: null, quantity: 1 }]);
    const [submitting, setSubmitting] = useState(false);
    const lineCounter = useRef(1);

    // Reset everything each time the modal is (re)opened — it's a single persistent instance
    // whose `show` prop toggles, so nothing here clears itself on its own between uses.
    useEffect(() => {
        if (!show) return;
        setPickedPatient(null);
        setReturnableItems([]);
        setLines([{ id: 'l0', orderableId: null, quantity: 1 }]);
        lineCounter.current = 1;
        methods.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    useEffect(() => {
        if (!show || !patientId) return;
        setLines([{ id: 'l0', orderableId: null, quantity: 1 }]);
        lineCounter.current = 1;
        setFetching(true);
        axios.get(`${API_BASE_URL}/api/v1/returns/returnable-items`, {
            withCredentials: true,
            params: { patientId },
        })
            .then(({ data }) => setReturnableItems(data))
            .catch(() => toast.error('Erreur lors du chargement des achats du patient'))
            .finally(() => setFetching(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, patientId]);

    // Aggregates the flat, per-order-item list into one entry per product — `occurrences`
    // keeps the underlying order lines (already oldest-first, per the API) for the allocation
    // pass at submit time.
    const aggregated = useMemo(() => {
        const map = new Map();
        returnableItems.forEach(it => {
            if (!map.has(it.orderableId)) {
                map.set(it.orderableId, {
                    orderableId: it.orderableId,
                    name: it.orderableName,
                    presentation: it.presentation,
                    dosage: it.dosage,
                    laboratoryId: it.laboratoryId,
                    laboratoryName: it.laboratoryName,
                    unitPrice: it.unitPrice,
                    maxReturnable: 0,
                    occurrences: [],
                });
            }
            const agg = map.get(it.orderableId);
            agg.maxReturnable += it.remainingQuantity;
            agg.occurrences.push(it);
        });
        return [...map.values()];
    }, [returnableItems]);

    const productOptions = useMemo(() => aggregated.map(a => ({
        value: a.orderableId,
        label: a.name,
        subtitle: [a.presentation, a.dosage].filter(Boolean).join(' · ') || null,
        laboratory: a.laboratoryId ? { id: a.laboratoryId, name: a.laboratoryName } : null,
    })), [aggregated]);

    const addLine = () => {
        const id = `l${lineCounter.current++}`;
        setLines(prev => [...prev, { id, orderableId: null, quantity: 1 }]);
    };

    const removeLine = (id) => setLines(prev => prev.filter(l => l.id !== id));

    const updateLine = (id, field, value) => {
        setLines(prev => prev.map(l => l.id === id ? { ...l, [field]: value } : l));
    };

    // Caps a line at what's still returnable for its product once quantities already entered
    // on OTHER lines for that same product are subtracted — otherwise picking the same
    // product twice could let the pair jointly ask for more than was actually bought.
    const maxForLine = (line) => {
        if (!line.orderableId) return 0;
        const agg = aggregated.find(a => a.orderableId === Number(line.orderableId));
        if (!agg) return 0;
        const usedElsewhere = lines
            .filter(l => l.id !== line.id && Number(l.orderableId) === Number(line.orderableId))
            .reduce((sum, l) => sum + (Number(l.quantity) || 0), 0);
        return Math.max(0, agg.maxReturnable - usedElsewhere);
    };

    const validLines = lines.filter(l => l.orderableId && Number(l.quantity) > 0);

    const total = validLines.reduce((sum, l) => {
        const agg = aggregated.find(a => a.orderableId === Number(l.orderableId));
        return sum + (agg ? Number(l.quantity) * Number(agg.unitPrice ?? 0) : 0);
    }, 0);

    const handleSubmit = async () => {
        if (!validLines.length) {
            toast.warning('Ajoutez au moins un produit à retourner');
            return;
        }

        // Merge lines targeting the same product (duplicate selection) before allocating, so
        // each product's occurrences are only walked through once.
        const requestedByOrderable = new Map();
        validLines.forEach(l => {
            const id = Number(l.orderableId);
            requestedByOrderable.set(id, (requestedByOrderable.get(id) || 0) + Number(l.quantity));
        });

        // Greedily consumes the oldest order first for each product, only spilling into a
        // later order once the current one runs out of returnable quantity for it.
        const perOrder = new Map();
        requestedByOrderable.forEach((qtyNeeded, orderableId) => {
            const agg = aggregated.find(a => a.orderableId === orderableId);
            if (!agg) return;
            let remaining = qtyNeeded;
            for (const occ of agg.occurrences) {
                if (remaining <= 0) break;
                const take = Math.min(occ.remainingQuantity, remaining);
                if (take <= 0) continue;
                remaining -= take;
                if (!perOrder.has(occ.orderId)) {
                    perOrder.set(occ.orderId, { items: [] });
                }
                perOrder.get(occ.orderId).items.push({ orderItemId: occ.orderItemId, quantity: take });
            }
        });

        const reason = methods.getValues('reason') || '';
        const entries = [...perOrder.entries()];
        setSubmitting(true);
        let succeeded = 0;
        try {
            // Sequential, not Promise.all — each POST is its own committed return (stock
            // already restored, order possibly canceled), so a later failure must not be
            // reported as if nothing had happened.
            for (const [orderId, data] of entries) {
                await axios.post(`${API_BASE_URL}/api/v1/returns`, {
                    orderId: Number(orderId),
                    reason,
                    items: data.items,
                }, { withCredentials: true });
                succeeded++;
            }
            toast.success(
                entries.length > 1
                    ? `Retour créé avec succès, réparti sur ${entries.length} ventes`
                    : 'Retour créé avec succès'
            );
            onClose();
            onSave?.();
        } catch (err) {
            const msg = err?.response?.data?.message || 'Erreur lors de la création du retour';
            if (succeeded > 0) {
                toast.error(`${msg} — ${succeeded}/${entries.length} vente(s) déjà traitée(s), vérifiez l'onglet Retours`);
                onSave?.();
            } else {
                toast.error(msg);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const title = pickedPatient ? `Créer un retour · ${pickedPatient.label}` : 'Créer un retour';

    return (
        <Modal
            show={show}
            onClose={onClose}
            title={title}
            classSize="exchange-modal-dialog"
            icon={faRotateLeft}
            iconBg="#fef2f2"
            iconColor="#991b1b"
            footer={patientId ? (
                <Button
                    isLoading={submitting}
                    loadingMessage="Enregistrement…"
                    className="btn btn-danger w-100"
                    onClick={handleSubmit}
                    disabled={validLines.length === 0}
                >
                    Créer le retour{validLines.length > 0 && ` (${formatAmount(total)} dhs)`}
                </Button>
            ) : null}
        >
            <FormProvider {...methods}>
                {!fixedPatientId && !patientId ? (
                    <div className="exchange-modal">
                        <p style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 12 }}>
                            Choisissez d'abord le patient concerné.
                        </p>
                        <label className="exchange-modal__notes-label">Patient</label>
                        <InputPatient
                            name="patient"
                            handleChange={(selected) => setPickedPatient(selected ?? null)}
                        />
                    </div>
                ) : fetching ? (
                    <p style={{ textAlign: 'center', color: '#9ca3af', padding: '24px 0' }}>Chargement…</p>
                ) : aggregated.length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#9ca3af', padding: '24px 0' }}>
                        Aucun produit retournable pour ce patient
                    </p>
                ) : (
                    <div className="exchange-modal">
                        <p style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 12 }}>
                            Sélectionnez le ou les produits à retourner — le système retrouve automatiquement
                            dans quelle(s) vente(s) du patient ils ont été achetés.
                        </p>

                        <div className="exchange-modal__lines-header">
                            <span className="exchange-modal__col-product">Produit</span>
                            <span className="exchange-modal__col-qty">Quantité</span>
                            <span className="exchange-modal__col-action" />
                        </div>

                        <div className="mb-3">
                            {lines.map(line => {
                                const max = maxForLine(line);
                                return (
                                    <div key={line.id} className="exchange-modal__line">
                                        <div className="exchange-modal__col-product">
                                            <ImprovedSelect
                                                name={line.id}
                                                options={productOptions}
                                                multiple={false}
                                                placeholder="Rechercher un produit acheté..."
                                                onChange={(selected) => updateLine(line.id, 'orderableId', selected?.value ?? null)}
                                            />
                                        </div>
                                        <div className="exchange-modal__col-qty">
                                            <input
                                                type="number"
                                                min="1"
                                                max={max}
                                                title={line.orderableId ? `${max} disponible(s) au retour` : undefined}
                                                className="form-control form-control-sm"
                                                value={line.quantity}
                                                disabled={!line.orderableId}
                                                onChange={e => {
                                                    const n = Math.max(0, Math.min(parseInt(e.target.value) || 0, max));
                                                    updateLine(line.id, 'quantity', n);
                                                }}
                                            />
                                        </div>
                                        <div className="exchange-modal__col-action">
                                            <button
                                                type="button"
                                                className="modal-icon-btn-danger"
                                                onClick={() => removeLine(line.id)}
                                                disabled={lines.length <= 1}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <button type="button" className="exchange-modal__add-btn mb-3" onClick={addLine}>
                            <FontAwesomeIcon icon={faPlus} />
                            Ajouter un produit
                        </button>

                        <hr className="exchange-modal__divider" />

                        <div>
                            <label className="exchange-modal__notes-label">Motif du retour (optionnel)</label>
                            <Textarea
                                name="reason"
                                placeholder="Précisez la raison du retour (optionnel)"
                            />
                        </div>
                    </div>
                )}
            </FormProvider>
        </Modal>
    );
}
